import React, { useCallback, useEffect, useState } from "react";

import * as api from "../api";
import Button from "../components/Button";
import Pill from "../components/Pill";
import type { PillTone } from "../logic";
import type { PageProps } from "./types";

function statusTone(s: string | null): PillTone {
  if (s === "completed" || s === "ended") return "ok";
  if (s === "failed") return "danger";
  return "warn";
}

function fmtDur(sec: number | null): string {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const seg = (active: boolean): React.CSSProperties => ({
  fontFamily: "var(--font-sans)",
  fontWeight: 600,
  fontSize: 13,
  padding: "7px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  background: active ? "#fff" : "transparent",
  color: active ? "var(--violet-500)" : "var(--text-muted)",
  boxShadow: active ? "var(--elevation-sm)" : "none",
});

const th: React.CSSProperties = { textAlign: "left", fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 400, padding: "8px 10px", borderBottom: "1px solid var(--border-hairline)" };
const td: React.CSSProperties = { padding: "11px 10px", borderBottom: "1px solid var(--border-hairline)" };
const inputStyle: React.CSSProperties = { flex: 1, fontFamily: "var(--font-mono)", fontSize: 13.5, color: "var(--ink-900)", background: "#fff", border: "1px solid var(--border-strong)", borderRadius: 8, padding: "11px 13px", outline: "none" };

export default function Conversations({ effectiveRole, intent }: PageProps) {
  const allowed = effectiveRole === "SUPERADMIN";
  const [mode, setMode] = useState<"byId" | "byOrg">("byId");
  const [convId, setConvId] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgLabel, setOrgLabel] = useState("");
  const [orgResults, setOrgResults] = useState<api.OrgRow[]>([]);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgSearching, setOrgSearching] = useState(false);
  const [rows, setRows] = useState<api.ConversationMetadata[]>([]);
  const [selected, setSelected] = useState<api.ConversationMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listByOrg = useCallback(async (oid: string) => {
    setError(null);
    try {
      const res = await api.listOrgConversations(oid);
      setRows(res.conversations);
      setSelected(res.conversations[0] || null);
    } catch (e: any) {
      setError(e?.message || "Lookup failed");
      setRows([]);
      setSelected(null);
    }
  }, []);

  // Preselect from an Orgs "Recent conversations" jump (superadmin only).
  useEffect(() => {
    if (!allowed || !intent?.conversationsOrgId) return;
    const oid = intent.conversationsOrgId;
    setMode("byOrg");
    setOrgId(oid);
    api.getOrg(oid).then((o) => setOrgLabel(o.name || oid), () => setOrgLabel(oid));
    listByOrg(oid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  // Debounced org search for the by-org picker — hits the backend per keystroke so
  // ANY org is reachable (not just the first page), and includes personal
  // workspaces so a solo user's calls are findable. Pauses once a pick is committed.
  useEffect(() => {
    if (!allowed || mode !== "byOrg" || orgId || !orgOpen) return;
    let active = true;
    setOrgSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.searchOrgs(orgLabel);
        if (active) setOrgResults(r.orgs);
      } catch {
        if (active) setOrgResults([]);
      } finally {
        if (active) setOrgSearching(false);
      }
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [orgLabel, orgId, orgOpen, mode, allowed]);

  const lookupById = async () => {
    const id = convId.trim();
    if (!id) { setError("Enter a conversation id."); return; }
    setError(null);
    try {
      const c = await api.getConversation(id);
      setRows([c]);
      setSelected(c);
    } catch (e: any) {
      setError(e?.status === 404 ? `No conversation found for “${id}”.` : e?.message || "Lookup failed");
      setRows([]);
      setSelected(null);
    }
  };

  if (!allowed) {
    return (
      <div style={{ padding: "28px 32px 48px", maxWidth: 1120 }}>
        <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 14, padding: 44, textAlign: "center" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--ink-300)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          <h2 style={{ margin: "0 0 6px", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 18, color: "var(--ink-900)" }}>Superadmin only</h2>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13.5 }}>Cross-org call access is restricted to superadmins. Turn off “view as staff” to continue.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 32px 48px", maxWidth: 1120 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, background: "var(--ink-900)", color: "var(--text-on-dark)", borderRadius: 14, padding: "16px 20px", marginBottom: 24 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--sky-400)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", marginTop: 1 }}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Metadata only — a permanent privacy boundary</div>
          <p style={{ margin: "5px 0 0", fontSize: 12.5, color: "rgba(240,238,245,0.72)", lineHeight: 1.55, maxWidth: 760 }}>
            Ops can see operational metadata to diagnose reported calls. Transcript, preview, debrief and analysis are <b style={{ color: "#fff" }}>never</b> accessible to ops (two-party-consent recording). Every lookup below is audited.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ display: "inline-flex", background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: 10, padding: 3, marginBottom: 16 }}>
            <button type="button" onClick={() => { setMode("byId"); setRows([]); setSelected(null); setError(null); }} style={seg(mode === "byId")}>By id</button>
            <button type="button" onClick={() => { setMode("byOrg"); setRows([]); setSelected(null); setError(null); }} style={seg(mode === "byOrg")}>By org</button>
          </div>

          {mode === "byId" ? (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <input value={convId} onChange={(e) => setConvId(e.target.value)} placeholder="Conversation id (UUID)" style={inputStyle} onKeyDown={(e) => { if (e.key === "Enter") lookupById(); }} />
                <Button variant="primary" onClick={lookupById}>Look up</Button>
              </div>
              <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-faint)" }}>Paste a conversation UUID from a support ticket.</p>
            </>
          ) : (
            <div style={{ position: "relative", marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  value={orgLabel}
                  onChange={(e) => { setOrgLabel(e.target.value); setOrgId(""); setOrgOpen(true); }}
                  onFocus={() => setOrgOpen(true)}
                  onBlur={() => setTimeout(() => setOrgOpen(false), 150)}
                  placeholder="Search org by name or id…"
                  style={{ ...inputStyle, fontFamily: "var(--font-sans)" }}
                />
                <Button variant="primary" disabled={!orgId} onClick={() => orgId && listByOrg(orgId)}>List</Button>
              </div>
              {orgOpen && !orgId && (
                <ul style={{ listStyle: "none", margin: "4px 0 0", padding: 4, position: "absolute", zIndex: 5, left: 0, right: 96, background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 8, boxShadow: "var(--elevation-md)", maxHeight: 240, overflow: "auto" }}>
                  {orgSearching && <li style={{ color: "var(--text-muted)", padding: "6px 8px", fontSize: 13 }}>searching…</li>}
                  {!orgSearching && orgResults.map((o) => (
                    <li key={o.id} onMouseDown={() => { setOrgId(o.id); setOrgLabel(o.name || o.id); setOrgOpen(false); }} style={{ padding: "6px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13 }}>
                      <span style={{ color: "var(--ink-900)" }}>{o.name || o.id}</span>{o.is_personal && <span style={{ color: "var(--text-muted)" }}> · personal</span>}{" "}
                      <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 11 }}>{o.id}</span>
                    </li>
                  ))}
                  {!orgSearching && orgResults.length === 0 && <li style={{ color: "var(--text-muted)", padding: "6px 8px", fontSize: 13 }}>no matches</li>}
                </ul>
              )}
            </div>
          )}

          {error && <p style={{ color: "var(--danger-500)", fontSize: 13, background: "var(--danger-100)", borderRadius: 8, padding: "10px 12px" }}>{error}</p>}

          {rows.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Call</th><th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>Duration</th>
              </tr></thead>
              <tbody>
                {rows.map((c) => {
                  const sel = selected?.id === c.id;
                  return (
                    <tr key={c.id} onClick={() => setSelected(c)} style={{ cursor: "pointer", background: sel ? "var(--surface-sunken)" : "transparent", boxShadow: sel ? "inset 3px 0 0 var(--violet-500)" : "none" }}>
                      <td style={td}>
                        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 700, color: "var(--ink-900)" }}>{c.id.slice(0, 12)}…</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{c.org_name || c.org_id} · {c.owner_name || c.owner_email || c.owner_id}</div>
                      </td>
                      <td style={td}><Pill tone={statusTone(c.status)}>{c.status || "—"}</Pill></td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" }}>{fmtDur(c.duration_seconds)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ position: "sticky", top: 0 }}>
          {selected ? (
            <div style={{ background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 16, padding: "22px 24px", boxShadow: "var(--elevation-sm)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--ink-900)", wordBreak: "break-all" }}>{selected.id}</div>
                <Pill tone={statusTone(selected.status)}>{selected.status || "—"}</Pill>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
                <tbody>
                  {[
                    ["Org", `${selected.org_name || "—"} · ${selected.org_id}`],
                    ["Owner", `${selected.owner_name || "—"}${selected.owner_email ? ` · ${selected.owner_email}` : ""}`],
                    ["Source", selected.source || "—"],
                    ["Dialed", selected.dialed_number || "—"],
                    ["Created", selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"],
                    ["Ended", selected.ended_at ? new Date(selected.ended_at).toLocaleString() : "—"],
                    ["Duration", fmtDur(selected.duration_seconds)],
                    ["Call id", selected.call_sid || "—"],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <th style={{ textAlign: "left", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 400, padding: "8px 12px 8px 0", verticalAlign: "top", whiteSpace: "nowrap" }}>{k}</th>
                      <td style={{ padding: "8px 0", fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--ink-900)", wordBreak: "break-all" }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 16, display: "flex", alignItems: "flex-start", gap: 10, background: "var(--ink-900)", borderRadius: 12, padding: "14px 16px" }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#8A8398" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", marginTop: 1 }}><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, lineHeight: 1.6, color: "#8A8398" }}>Recorded content locked<br /><span style={{ color: "#6E6880" }}>transcript · preview · debrief · analysis are not accessible to ops</span></div>
              </div>
            </div>
          ) : (
            <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 16, padding: "40px 24px", textAlign: "center", color: "var(--text-faint)", fontSize: 13.5 }}>Look up a call to see its metadata.</div>
          )}
        </div>
      </div>
    </div>
  );
}
